import { useState } from "react";
import { CalendarCheck, CheckCircle2, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAMP_PATTERNS } from "@/lib/constants";

const TIME_WINDOWS = ["Morning (8–11 AM)", "Midday (11 AM–2 PM)", "Afternoon (2–5 PM)", "Evening (5–7 PM)"];
const initial = { name: "", phone: "", email: "", city: "", address: "", preferred_date: "", time_window: "", pattern: "", notes: "" };

export default function ConsultScheduler({ selectedColor }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.preferred_date || !form.time_window) {
      toast.error("Please add your name, phone, email, and a preferred date & time.");
      return;
    }
    setSubmitting(true);
    try {
      const patternLabel = form.pattern || "Help me choose";
      const styleInterest = `${patternLabel} · ${selectedColor?.name || "color TBD"}`;
      const details =
        `🗓️ FREE ON-SITE CONSULT REQUEST\n` +
        `Preferred date: ${form.preferred_date}\n` +
        `Preferred time: ${form.time_window}\n` +
        `Style interest: ${styleInterest}` +
        (form.notes ? `\n\nNotes: ${form.notes}` : "");
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_type: "Stamped Concrete",
        city: form.city,
        address: form.address,
        timeline: form.preferred_date,
        budget: "",
        project_details: details,
        inspiration_projects: [styleInterest],
        source: "stamp-studio-consult",
      });
      setDone(true);
      toast.success("Consult request sent — we'll confirm your time shortly!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-card border border-border p-10 text-center" data-testid="consult-success">
        <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-bold mb-2">Consult Requested</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Thanks, {form.name.split(" ")[0]}! We'll reach out to confirm your {form.preferred_date} visit and bring physical samples of {selectedColor?.name || "your chosen color"}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-card border border-border p-6 sm:p-8 space-y-5" data-testid="consult-form">
      {selectedColor && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 p-3.5" data-testid="consult-selection">
          <span className="h-8 w-8 rounded-sm ring-1 ring-black/10 shrink-0" style={{ backgroundColor: selectedColor.hex }} />
          <p className="text-sm">
            <span className="text-muted-foreground">You're viewing </span>
            <span className="font-bold">{selectedColor.name}</span>
            <span className="text-muted-foreground"> — we'll bring a matching sample to your visit.</span>
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">Full Name *</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Jane Homeowner" className="mt-1.5 rounded-sm" data-testid="consult-name" />
        </div>
        <div>
          <Label className="font-semibold">Phone *</Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="(920) 555-0123" className="mt-1.5 rounded-sm" data-testid="consult-phone" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">Email *</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5 rounded-sm" data-testid="consult-email" />
        </div>
        <div>
          <Label className="font-semibold">Project Address / City</Label>
          <Input value={form.city} onChange={set("city")} placeholder="Watertown, WI" className="mt-1.5 rounded-sm" data-testid="consult-city" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold flex items-center gap-1.5"><CalendarCheck className="h-4 w-4 text-primary" /> Preferred Date *</Label>
          <Input type="date" min={today} value={form.preferred_date} onChange={set("preferred_date")} className="mt-1.5 rounded-sm" data-testid="consult-date" />
        </div>
        <div>
          <Label className="font-semibold flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" /> Time Window *</Label>
          <Select value={form.time_window} onValueChange={set("time_window")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="consult-time"><SelectValue placeholder="Pick a time" /></SelectTrigger>
            <SelectContent>
              {TIME_WINDOWS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="font-semibold">Pattern You Like</Label>
        <Select value={form.pattern} onValueChange={set("pattern")}>
          <SelectTrigger className="mt-1.5 rounded-sm" data-testid="consult-pattern"><SelectValue placeholder="Help me choose" /></SelectTrigger>
          <SelectContent>
            {STAMP_PATTERNS.map((p) => <SelectItem key={p.slug} value={p.name} data-testid={`consult-pattern-${p.slug}`}>{p.name}</SelectItem>)}
            <SelectItem value="Help me choose">Help me choose</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="font-semibold">Anything else? (optional)</Label>
        <Textarea value={form.notes} onChange={set("notes")} placeholder="Square footage, current condition, gate codes, best contact time…" rows={3} className="mt-1.5 rounded-sm" data-testid="consult-notes" />
      </div>

      <Button type="submit" disabled={submitting} className="w-full h-12 bg-primary hover:bg-primary/90 rounded-sm font-bold text-base" data-testid="consult-submit">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Book My Free On-Site Consult"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">Free &amp; no-obligation. We'll confirm your exact time by phone within one business day.</p>
    </form>
  );
}
